import type { ConversationMessage } from "@lupp/shared";

/** id → message, for the OWUI conversation tree of a loaded chat. */
export type NodeMap = Record<string, ConversationMessage>;

export function toNodeMap(messages: ConversationMessage[]): NodeMap {
  const map: NodeMap = {};
  for (const m of messages) map[m.id] = m;
  return map;
}

function rootsOf(nodes: NodeMap): string[] {
  return Object.values(nodes)
    .filter((m) => m.parentId == null)
    .map((m) => m.id);
}

/** Siblings of a message: its parent's children, or the root set for top-level nodes. */
export function siblingsOf(nodes: NodeMap, message: ConversationMessage): string[] {
  if (message.parentId != null && nodes[message.parentId]) {
    return nodes[message.parentId].childrenIds ?? [];
  }
  return rootsOf(nodes);
}

/** Walk down from `startId`, following the LAST child at each step → leaf id (OWUI behaviour). */
export function leafOf(nodes: NodeMap, startId: string): string {
  let id = startId;
  // guard against cycles
  for (let i = 0; i < 10000; i++) {
    const children = nodes[id]?.childrenIds ?? [];
    if (children.length === 0) break;
    id = children[children.length - 1];
  }
  return id;
}

/**
 * Active linear path root→`currentId` (via parentId), with each message annotated with
 * `branch` info when it is one of several sibling variants. Falls back to insertion order
 * when there is no usable tree (non-forked / legacy chats).
 */
export function buildActivePath(nodes: NodeMap, currentId: string | null): ConversationMessage[] {
  if (!currentId || !nodes[currentId]) return Object.values(nodes);

  const path: ConversationMessage[] = [];
  let id: string | null = currentId;
  for (let i = 0; i < 10000 && id && nodes[id]; i++) {
    path.unshift(nodes[id]);
    id = nodes[id].parentId ?? null;
  }

  return path.map((m) => {
    const siblings = siblingsOf(nodes, m);
    if (siblings.length > 1) {
      return { ...m, branch: { index: siblings.indexOf(m.id) + 1, count: siblings.length } };
    }
    return m.branch ? { ...m, branch: undefined } : m;
  });
}

/**
 * Switch the branch at `messageId` by `dir` (-1 prev / +1 next) and return the new active
 * leaf id (descend the chosen sibling to its leaf). Returns null if no switch is possible.
 */
export function switchBranchLeaf(nodes: NodeMap, messageId: string, dir: -1 | 1): string | null {
  const node = nodes[messageId];
  if (!node) return null;
  const siblings = siblingsOf(nodes, node);
  if (siblings.length <= 1) return null;
  const i = siblings.indexOf(messageId);
  const j = i + dir;
  if (j < 0 || j >= siblings.length) return null;
  return leafOf(nodes, siblings[j]);
}
